import { z } from 'zod';
import { BrandedId, IsoInstant, ScenarioTypeSchema } from './common.js';

// GET /api/public/availability — values arrive via query string, so coerce numerics.
export const PublicAvailabilityRequest = z.object({
  venueId: BrandedId,
  serviceId: BrandedId,
  scenarioType: ScenarioTypeSchema,
  resourceId: BrandedId,
  partySize: z.coerce.number().int().positive(),
  startTime: IsoInstant,
  endTime: IsoInstant,
});
export type PublicAvailabilityRequest = z.infer<typeof PublicAvailabilityRequest>;

export const PublicAvailabilityResponse = z.object({
  venueId: BrandedId,
  serviceId: BrandedId,
  startTime: IsoInstant,
  endTime: IsoInstant,
  hasAvailability: z.boolean(),
  busyLevel: z.enum(['low', 'medium', 'high', 'full']),
  wordingKey: z.string(),
});
export type PublicAvailabilityResponse = z.infer<typeof PublicAvailabilityResponse>;

// POST /api/public/takeout-requests
export const CreateTakeoutRequestBody = z.object({
  venueId: BrandedId,
  customerRef: z.string().min(1),
  pickupTime: IsoInstant,
  itemNote: z.string().max(2000),
});
export type CreateTakeoutRequestBody = z.infer<typeof CreateTakeoutRequestBody>;

export const CreateTakeoutRequestResponse = z.object({
  requestId: BrandedId,
  status: z.literal('submitted'),
});
export type CreateTakeoutRequestResponse = z.infer<typeof CreateTakeoutRequestResponse>;

// POST /api/public/reservations
const PublicGuestContact = z
  .object({
    displayName: z.string().min(1).max(100),
    phone: z.string().min(3).max(50).optional(),
    email: z.string().email().max(200).optional(),
    lineId: z.string().min(1).max(50).optional(),
  })
  .strict()
  .refine((c) => Boolean(c.phone || c.email || c.lineId), {
    message: 'at least one of phone/email/lineId is required',
  });

export const CreatePublicReservationBody = z
  .object({
    venueId: BrandedId,
    serviceId: BrandedId,
    scenarioType: ScenarioTypeSchema,
    partySize: z.number().int().positive().max(20),
    startTime: IsoInstant,
    endTime: IsoInstant,
    guestContact: PublicGuestContact,
    notes: z.string().max(500).nullable().optional(),
  })
  .strict();
export type CreatePublicReservationBody = z.infer<typeof CreatePublicReservationBody>;

export const CreatePublicReservationResponse = z
  .object({
    reservationId: BrandedId,
    guestToken: z.string().min(20).max(64),
    status: z.enum(['pending', 'confirmed', 'checked_in', 'completed', 'canceled', 'no_show']),
    startTime: IsoInstant,
  })
  .strict();
export type CreatePublicReservationResponse = z.infer<typeof CreatePublicReservationResponse>;

// GET /api/public/reservations/:token
export const PublicReservationView = z
  .object({
    reservationId: BrandedId,
    scenarioType: ScenarioTypeSchema,
    status: z.enum(['pending', 'confirmed', 'checked_in', 'completed', 'canceled', 'no_show']),
    partySize: z.number().int().positive(),
    startTime: IsoInstant,
    endTime: IsoInstant,
    displayName: z.string().min(1),
    paymentStatus: z.enum(['unpaid', 'partial', 'paid', 'refunded']),
    totalAmount: z.number().int().nonnegative().nullable(),
    currency: z.enum(['TWD']).nullable(),
  })
  .strict();
export type PublicReservationView = z.infer<typeof PublicReservationView>;

// POST /api/public/reservations/:token/cancel
export const CancelPublicReservationBody = z
  .object({
    reason: z.string().max(200).nullable().optional(),
  })
  .strict();
export type CancelPublicReservationBody = z.infer<typeof CancelPublicReservationBody>;

export const CancelPublicReservationResponse = z
  .object({
    status: z.literal('canceled'),
  })
  .strict();
export type CancelPublicReservationResponse = z.infer<typeof CancelPublicReservationResponse>;
